import { Alert, Dialog, Grid, InputAdornment, Skeleton, Stack, TextField } from '@all-manager/design-system-v2';
import { Controller } from "react-hook-form";
import DateField from "../../../../../components/modals/components/DateFieldOk";
import DescriptionFieldOk from "../../../../../components/modals/components/DescriptionFieldOk";
import type { DocumentMinimalData, SubjectHistoryFeed } from "../../../../../types";
import DocumentsFormSection from "../../../../documents/external-components/DocumentsFormSection/DocumentsFormSection";
import ProviderAutocomplete from "../../../../providers/external-components/ProviderAutocomplete/ProviderAutocomplete";
import useAddQuoteReceivedActionModal from "./useAddQuoteReceivedActionModal";

export type InitialQuoteReceivedValues = {
  date?: Date | string;
  amount?: number | null;
  comment?: string;
  isAiGeneratedDescription?: boolean;
  provider_id?: string | null;
  defaultProviderSearch?: string | null;
  documents?: DocumentMinimalData[];
};

type Props = {
  onClose: () => void;
  subjectId: string;
  item?: SubjectHistoryFeed;
  initialValues?: InitialQuoteReceivedValues;
  onSuccess?: () => void;
  open?: boolean;
}

const EURO_ADORNMENT = { input: { endAdornment: <InputAdornment position="end">€</InputAdornment> } };

export default function AddQuoteReceivedActionModal(props: Props) {
  const { form, handleConfirm, isLoading, serverError, isDocumentLoading } = useAddQuoteReceivedActionModal(props);

  return (
    // Also opened from the inbox processing workflow.
    <Dialog
      open={props.open ?? true}
      onClose={props.onClose}
      maxWidth="sm"
      fullWidth
      title={props.item ? "Modifier la réception de devis" : "Nouvelle réception de devis"}
      closeLabel="Fermer"
      actions={{
        primaryAction: {
          label: 'Enregistrer',
          onClick: form.handleSubmit(handleConfirm),
          loading: isLoading,
          disabled: isDocumentLoading,
        },
        secondaryAction: {
          label: 'Annuler',
          onClick: props.onClose,
          disabled: isLoading,
        },
      }}
    >
      <Stack spacing={2}>
        {serverError ? <Alert severity="error">{serverError}</Alert> : null}

        <DateField
          name="date"
          form={form}
          label="Date de réception"
          isTimeManaged={false}
          required
        />

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 7 }}>
            <Controller
              control={form.control}
              name='provider_id'
              render={({ field, fieldState }) => (
                <ProviderAutocomplete
                  label="Prestataire"
                  fullWidth
                  value={field.value ?? null}
                  onChange={(value) => field.onChange(value)}
                  error={!!fieldState.error}
                  helperText={fieldState.error?.message}
                  defaultSearch={props.initialValues?.defaultProviderSearch ?? props.item?.provider_name ?? undefined}
                />
              )}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: 5 }}>
            <Controller
              control={form.control}
              name='amount'
              render={({ field, fieldState }) => (
                <TextField
                  name={field.name}
                  inputRef={field.ref}
                  onBlur={field.onBlur}
                  label="Montant"
                  type="number"
                  fullWidth
                  value={field.value ?? ''}
                  onChange={(event) => field.onChange(event.target.value ? parseFloat(event.target.value) : undefined)}
                  helperText={fieldState.error?.message}
                  error={!!fieldState.error}
                  slotProps={EURO_ADORNMENT}
                />
              )}
            />
          </Grid>
        </Grid>

        <DescriptionFieldOk
          form={form}
          name="comment"
          label="Commentaire"
          isAiGenerated={Boolean(props.initialValues?.isAiGeneratedDescription)}
        />

        {isDocumentLoading ? (
          <Skeleton variant="rounded" height={120} aria-label="Chargement des documents" />
        ) : (
          <Controller
            name='documents'
            control={form.control}
            render={({ field }) => (
              <DocumentsFormSection
                documents={field.value}
                onChange={field.onChange}
                subjectId={props.subjectId}
                defaultDocumentType="Devis"
                hideType
              />
            )}
          />
        )}
      </Stack>
    </Dialog>
  );
}
